// api/framework-library.ts
//
// The framework library (#139, backend app/services/framework_library): every
// approved framework as an anonymised template (no client name, competitor
// names, URLs, quoted evidence, figures or proper nouns), by category, with its
// measured performance. Templates are read-only history; an admin can only
// mark one as a recommended starting point or exclude it. Consent is here too:
// a client's opt-out, and sources kept out of one brand's generations.
// Admin-only.

import { apiFetch } from "./api-fetch";

export type TemplateMark = "recommended" | "excluded";

export interface TemplateSummary {
  id: number;
  source: "approval" | "rebaseline" | "backfill";
  source_detail: string;
  category: string;
  vertical: string;
  generated: boolean;
  approved_at: string;
  mark: TemplateMark | null;
  mark_reason: string;
  marked_by: string | null;
  marked_at: string | null;
  performance_updated_at: string | null;
  beliefs: number;
  personas: number;
  questions: number;
  reads: number;
  answers: number;
  /** Scorer model -> answers it judged. */
  judges: Record<string, number>;
  mean_presence: number | null;
  calibrations: number;
  first_calibration_state: string | null;
  beliefs_clean_first_time: number | null;
  beliefs_calibrated: number | null;
  category_defaults: number | null;
}

export interface TemplateBelief {
  id: string;
  label: string;
  claim: string;
  why_it_matters: string;
  anchors: { score_0: string; score_1: string; score_2: string };
  examples: Record<string, string>;
  evidence_shape: { quotes: number; from_pages: number; from_documents: number };
  category_default: boolean | null;
}

export interface TemplateQuery {
  id: string;
  text: string;
  form: string;
  persona_id: string;
  use_case: string;
  belief_ids: string[];
}

export interface BeliefCell {
  answers: number;
  present: number;
  share: number | null;
  primary_when_present: number | null;
  directional: boolean;
  primary_directional: boolean;
}

export interface TemplatePerformance {
  reads?: number;
  answers?: number;
  judges?: Record<string, number>;
  beliefs?: Record<string, BeliefCell>;
  questions?: Record<string, { answers: number; beliefs: Record<string, number>; unprompted: string[] }>;
  forms?: Record<string, { answers: number; beliefs: Record<string, number> }>;
  calibration?: {
    count: number;
    first_state: string | null;
    latest_state?: string | null;
    beliefs: Record<string, { first_flags: string[]; clean_first_time: boolean; latest_flags: string[] }>;
  };
}

export interface TemplateDetail extends TemplateSummary {
  content: {
    category: string;
    beliefs: TemplateBelief[];
    personas: { id: string; label: string; description: string; goal: string; use_case: string }[];
    battery: { queries: TemplateQuery[]; forms: Record<string, number> };
    name_variants: { count: number; pattern: string[]; misidentification_gates: number };
  };
  performance: TemplatePerformance;
}

export interface CategoryBelief {
  template_id: number;
  id: string;
  label: string;
  claim: string;
  category_default: boolean | null;
  presence: number | null;
  primary_when_present: number | null;
  answers: number;
  directional: boolean | null;
  judges: string[];
  clean_first_time: boolean | null;
  first_flags: string[] | null;
}

export interface Onboarding {
  generated_at: string | null;
  approved_at: string | null;
  library_templates: number;
  regenerations: number;
  edit_count: number | null;
  by_part: Record<string, number>;
}

export interface LibraryCategory {
  key: string;
  category: string;
  vertical: string;
  vertical_label: string;
  templates: TemplateSummary[];
  beliefs: CategoryBelief[];
  onboardings: Onboarding[];
}

export interface LibraryView {
  available: boolean;
  categories: LibraryCategory[];
  /** Stored templates not shown, by why. Their text is never served. */
  withheld?: { older_anonymiser: number; source_not_a_client: number; source_opted_out: number };
  /** Approvals the anonymiser gate refused, still with no template. Counts only. */
  refused?: { count: number; leaks: Record<string, number> };
}

export interface LibraryClient {
  customer_id: number;
  brand_name: string;
  /** Only on main-v2; null means a client. */
  kind: string | null;
  opted_out: boolean;
  opted_out_by: string | null;
  opted_out_at: string | null;
  /** Templates kept out of this brand's generations. */
  excluded_templates: number[];
}

export interface FrameworkLibrarySettings {
  /** How many templates a Stage 2 generation is given; 0 = no lookup. */
  templates_per_generation: number;
  source: "admin" | "built_in";
  default: number;
  max: number;
}

export const frameworkLibraryApi = {
  view: () => apiFetch<LibraryView>("/framework-library"),
  template: (id: number) => apiFetch<TemplateDetail>(`/framework-library/templates/${id}`),
  mark: (id: number, mark: TemplateMark | null, reason: string) =>
    apiFetch<TemplateSummary>(`/framework-library/templates/${id}/mark`, {
      method: "POST",
      body: JSON.stringify({ mark, reason }),
    }),
  settings: () => apiFetch<FrameworkLibrarySettings>("/admin/framework-library-settings"),
  /** null goes back to the built-in default. */
  saveSettings: (templates_per_generation: number | null) =>
    apiFetch<FrameworkLibrarySettings>("/admin/framework-library-settings", {
      method: "PUT",
      body: JSON.stringify({ templates_per_generation }),
    }),
  refresh: () => apiFetch<{ updated: number }>("/framework-library/refresh", { method: "POST" }),
  clients: () => apiFetch<{ clients: LibraryClient[] }>("/framework-library/clients"),
  /** Opting out deletes the client's templates in the same call. */
  setOptOut: (customerId: number, opted_out: boolean) =>
    apiFetch<{ templates_removed: number }>(`/framework-library/clients/${customerId}/opt-out`, {
      method: "PUT",
      body: JSON.stringify({ opted_out }),
    }),
  excludeTemplate: (customerId: number, templateId: number, reason: string) =>
    apiFetch(`/framework-library/clients/${customerId}/exclusions/${templateId}`, {
      method: "PUT",
      body: JSON.stringify({ reason }),
    }),
  includeTemplate: (customerId: number, templateId: number) =>
    apiFetch(`/framework-library/clients/${customerId}/exclusions/${templateId}`, { method: "DELETE" }),
};
